"use client";

import React from "react";

import { ActionsMenuDropdown } from "@/components/flowbite/actions-menu-dropdown";
import { TruncatedTextWithTooltip } from "@/components/flowbite/truncated-text-with-tooltip";
import { cn } from "@/shared/helpers/cn.helper";

import type {
  LayersFiltersListGroupItem,
  LayersFiltersListItem,
  LayersFiltersListProps,
} from "./types";

function isGroup(
  v: LayersFiltersListItem | LayersFiltersListGroupItem,
): v is LayersFiltersListGroupItem {
  return "items" in v;
}

function Row({
  item,
  nested,
  variant = "default",
}: {
  item: LayersFiltersListItem;
  nested?: boolean;
  variant?: LayersFiltersListProps["variant"];
}) {
  const muted = Boolean(item.active);
  const interactiveHighlight = Boolean(item.interactiveHighlight);

  const hasIconClickHandler = Boolean(item.onIconClick);

  const handleItemClick = () => {
    (item.onItemClick ?? item.onIconClick)?.();
  };

  const handleRowKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleItemClick();
    }
  };

  const alignWithTitle = variant === "alignWithTitle";

  return (
    <div
      key={item.id}
      className={cn(
        "relative flex w-full cursor-pointer items-center gap-3 rounded-lg text-left transition-colors duration-300",
        alignWithTitle ? "py-2" : "px-2 py-2",
        { "bg-primary-soft": interactiveHighlight },
      )}
      role="button"
      tabIndex={0}
      onClick={handleItemClick}
      onKeyDown={handleRowKeyDown}
    >
      <div className={cn("flex min-w-0 grow items-center justify-between gap-2", { "pl-3": nested })}>
        <div className="flex min-w-0 flex-1 flex-col">
          <TruncatedTextWithTooltip
            className={cn(
              "text-sm font-medium transition-colors duration-300",
              { "text-btn-icon-inactive": muted, "text-btn-icon": !muted && !interactiveHighlight, "text-primary": interactiveHighlight },
            )}
            text={item.label}
          />
          {item.subtitle != null && item.subtitle !== "" && (
            <TruncatedTextWithTooltip
              className="text-[11px] font-medium leading-[14px] text-gray-700"
              text={item.subtitle}
            />
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {item.subactions && item.subactions.length > 0 && (
            <span className="inline-flex items-center gap-1">
              {item.subactions.map((subaction) => {
                const subMuted = Boolean(subaction.active);
                return (
                  <button
                    key={subaction.id}
                    aria-label={`Subaction ${subaction.id}`}
                    className={cn(
                      "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded transition-colors duration-300",
                      subMuted ? "[&_svg]:text-btn-icon-inactive" : "[&_svg]:text-btn-icon",
                    )}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      subaction.onClick?.();
                    }}
                  >
                    {subaction.icon}
                  </button>
                );
              })}
            </span>
          )}

          {item.icon && (
            hasIconClickHandler ? (
              <button
                aria-label={`Toggle visibility ${item.id}`}
                className={cn(
                  "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded transition-colors duration-300",
                  muted ? "[&_svg]:text-btn-icon-inactive" : "[&_svg]:text-btn-icon",
                )}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  item.onIconClick?.();
                }}
              >
                {item.icon}
              </button>
            ) : (
              <span
                className={cn(
                  "inline-flex h-6 w-6 shrink-0 items-center justify-center transition-colors duration-300",
                  muted ? "[&_svg]:text-btn-icon-inactive" : "[&_svg]:text-btn-icon",
                )}
              >
                {item.icon}
              </span>
            )
          )}

          {item.actionsMenu != null && item.actionsMenu.length > 0 && (
            <ActionsMenuDropdown
              actions={item.actionsMenu}
              footer={item.actionsMenuFooter}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export const LayersFiltersList: React.FC<LayersFiltersListProps> = ({
  items,
  variant = "default",
}) => {
  const alignWithTitle = variant === "alignWithTitle";

  return (
    <div className={alignWithTitle ? undefined : "px-2"}>
      {items.map((node, idx) => {
        if (isGroup(node)) {
          return (
            <div
              key={`group-${node.id}`}
              className={cn("py-3", { "border-t border-light-100 flex flex-col gap-2 w-full": idx !== 0 })}
            >
              {node.label != null && node.label !== "" && (
                <div className="px-2 text-sm font-medium text-text-primary">
                  {node.label}
                </div>
              )}

              <div className="flex flex-col gap-2">
                {node.items.map((it) => (
                  <Row
                    key={it.id}
                    nested
                    item={it}
                    variant={variant}
                  />
                ))}
              </div>
            </div>
          );
        }

        return (
          <div
            key={node.id}
            className={cn("flex w-full flex-col", idx !== 0 ? "border-light-100 border-t py-3" : "py-2")}
          >
            <Row
              item={node}
              variant={variant}
            />
          </div>
        );
      })}
    </div>
  );
};
